import React, { useState, useEffect } from 'react';
import { Phone, Calendar, DollarSign, Eye, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Employee } from '../../../types/employee';
import { contractService } from '../../../services/contractService';
import { formatCurrency } from '../../../utils/currencyUtils';

interface EmployeeCardProps {
  employee: Employee;
  getStatusColor: (status: string) => string;
  getStatusLabel: (status: string) => string;
}

export const EmployeeCard: React.FC<EmployeeCardProps> = ({
  employee,
  getStatusColor,
  getStatusLabel,
}) => {
  const navigate = useNavigate();
  const [eventos, setEventos] = useState<Array<{
    contractId: string;
    nomeAniversariante: string;
    dataFesta: string;
    valorPagamento: number;
    funcao: string;
  }>>([]);
  const [loadingEventos, setLoadingEventos] = useState(false);

  useEffect(() => {
    const loadEventos = async () => {
      try {
        setLoadingEventos(true);
        const employeeContracts = await contractService.getContractsByEmployee(employee.id);
        console.log('🔍 Eventos carregados para funcionário:', employee.nome, employeeContracts);
        setEventos(employeeContracts);
      } catch (error) {
        console.error('Erro ao carregar eventos do funcionário:', error);
      } finally {
        setLoadingEventos(false);
      }
    };

    loadEventos();
  }, [employee.id]);
  return (
    <div className="bg-white rounded-lg p-4 border border-slate-200 hover:shadow-md transition-shadow flex flex-col h-full">
      {/* Header do funcionário */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-teal-600 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-white">
              {employee.nome.split(' ').map(n => n[0]).join('').toUpperCase()}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-base font-semibold text-slate-900">{employee.nome}</span>
            </div>
            {employee.dataNascimento && (
              <div className="text-sm text-slate-600">
                {(() => {
                  try {
                    // Tentar diferentes formatos de data
                    let dateObj;
                    if (typeof employee.dataNascimento === 'string') {
                      // Se for string, tentar fazer parse
                      if (employee.dataNascimento.includes('/')) {
                        // Formato brasileiro DD/MM/YYYY
                        const [day, month, year] = employee.dataNascimento.split('/');
                        dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                      } else {
                        // Formato ISO ou outro
                        dateObj = new Date(employee.dataNascimento);
                      }
                    } else {
                      dateObj = new Date(employee.dataNascimento);
                    }
                    
                    // Verificar se a data é válida
                    if (!isNaN(dateObj.getTime())) {
                      return dateObj.toLocaleDateString('pt-BR');
                    } else {
                      console.warn('⚠️ Data de nascimento inválida:', employee.dataNascimento);
                      return 'Data inválida';
                    }
                  } catch (error) {
                    console.error('❌ Erro ao processar data de nascimento:', error);
                    return 'Data inválida';
                  }
                })()}
              </div>
            )}
          </div>
        </div>
        
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(employee.status)}`}>
          {getStatusLabel(employee.status)}
        </span>
      </div>
      
      {/* Informações de contato */}
      <div className="space-y-2 mb-3">
        <div className="flex items-center gap-2 text-sm">
          <Phone className="w-4 h-4 text-slate-400" />
          <span className="text-slate-700">{employee.telefone}</span>
        </div>
      </div>

      {/* Conteúdo flexível - cresce conforme necessário */}
      <div className="flex-1 flex flex-col">
        {/* Lista de Eventos */}
        {eventos.length > 0 ? (
          <div className="mb-3 border-t border-slate-100 pt-3">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-700">Eventos ({eventos.length})</span>
            </div>
            
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {eventos.map((evento, index) => (
                <div key={evento.contractId || index} className="flex items-center justify-between text-xs bg-slate-50 rounded p-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800 truncate">
                        {evento.nomeAniversariante}
                      </span>
                      <button
                        onClick={() => navigate(`/admin/contratos/${evento.contractId}`)}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded transition-colors"
                        title="Ver evento"
                      >
                        <Eye className="w-3 h-3" />
                        Ver
                      </button>
                    </div>
                    <div className="text-slate-500 text-xs">
                      {evento.funcao} • {(() => {
                        console.log('🔍 Processando data do evento:', evento.dataFesta, 'Tipo:', typeof evento.dataFesta);
                        
                        if (!evento.dataFesta || evento.dataFesta.trim() === '') {
                          return 'Data não informada';
                        }
                        
                        try {
                          // Tentar diferentes formatos de data
                          let dateObj;
                          if (typeof evento.dataFesta === 'string') {
                            // Se for string, tentar fazer parse
                            if (evento.dataFesta.includes('/')) {
                              // Formato brasileiro DD/MM/YYYY
                              const [day, month, year] = evento.dataFesta.split('/');
                              dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                            } else {
                              // Formato ISO ou outro
                              dateObj = new Date(evento.dataFesta);
                            }
                          } else {
                            dateObj = new Date(evento.dataFesta);
                          }
                          
                          // Verificar se a data é válida
                          if (!isNaN(dateObj.getTime())) {
                            return dateObj.toLocaleDateString('pt-BR');
                          } else {
                            console.warn('⚠️ Data inválida no evento:', evento.dataFesta);
                            return 'Data inválida';
                          }
                        } catch (error) {
                          console.error('❌ Erro ao processar data do evento:', error);
                          return 'Data inválida';
                        }
                      })()}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-emerald-600 font-medium ml-2">
                    <DollarSign className="w-3 h-3" />
                    <span>{formatCurrency(evento.valorPagamento.toString())}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : !loadingEventos && (
          /* Mensagem quando não há eventos */
          <div className="mb-3 border-t border-slate-100 pt-3 flex-1 flex items-center justify-center">
            <div className="text-center py-4">
              <Calendar className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500 font-medium">Ainda não foi escalado</p>
              <p className="text-xs text-slate-400">para nenhum evento</p>
            </div>
          </div>
        )}

        {/* Loading de eventos */}
        {loadingEventos && (
          <div className="mb-3 border-t border-slate-100 pt-3">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span>Carregando eventos...</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Ações */}
      <div className="flex items-center pt-3 border-t border-slate-100">
        <button
          onClick={() => navigate(`/admin/funcionarios/${employee.id}`)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors"
        >
          <User className="w-4 h-4" />
          Ver Detalhes
        </button>
      </div>
    </div>
  );
};
